<template>
  <div class="primaryLight--text mx-5 mt-3">
    <v-toolbar elevation="2" class="my-3">
      <template v-if="$vuetify.breakpoint.mdAndUp">
        <v-toolbar-title class="primaryLight--text">My Clients</v-toolbar-title>
        <v-divider class="mx-4" inset vertical></v-divider>
      </template>
      <v-spacer></v-spacer>
      <v-dialog v-model="newClientDialog" max-width="500px">
        <template v-slot:activator="{ on, attrs }">
          <v-btn color="primaryLight" depressed class="white--text" v-bind="attrs" v-on="on" @click="openNewClientDialog()">
            <v-icon class="mr-2">mdi-plus</v-icon>Add new Client
          </v-btn>
        </template>

        <v-card>
          <v-card-title class="primaryLight--text">Client basic details</v-card-title>
          <v-card-text class="pb-0">
            <v-container>
              <!-- START FORM -->
              <v-form v-model="newClientFormValid">
                <v-row>

                  <!-- START NAME FIELD -->
                  <v-col cols="12" class="pt-0">
                    <v-text-field
                      outlined dense color="primaryLight"
                      v-model="newClient.name" :rules="nameRules"
                      :error-messages="newClientErrors.name"
                      @focus="newClientErrors.name = []"
                      label="Name">
                    </v-text-field>
                  </v-col>

                  <!-- START NUMBER FIELD -->
                  <v-col cols="12" class="pt-0">
                    <v-text-field
                      outlined dense color="primaryLight"
                      v-model="newClient.contact_number" :rules="numberRules"
                      :error-messages="newClientErrors.contact_number"
                      @focus="newClientErrors.contact_number = []"
                      label="Contact number"
                      prefix="+673">
                    </v-text-field>
                  </v-col>

                  <!-- START BIRTHDATE FIELD -->
                  <v-col cols="12" class="pt-0">
                    <template>
                      <v-menu
                        ref="datepicker"
                        v-model="datepicker"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="290px">
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            outlined dense color="primaryLight"
                            v-model="newClient.birthdate"
                            label="Birthdate"
                            hint="This is in YYYY-MM-DD format"
                            persistent-hint
                            v-bind="attrs"
                            v-on="on">
                          </v-text-field>
                        </template>
                        <v-date-picker
                          ref="picker"
                          v-model="newClient.birthdate"
                          :max="new Date().toISOString().substr(0, 10)"
                          min="1940-01-01"
                          @change="setDate()"
                        ></v-date-picker>
                      </v-menu>
                    </template>
                  </v-col>

                  <!-- START ADDRESS FIELD -->
                  <v-col cols="12" class="pt-0">
                    <v-textarea
                      outlined dense color="primaryLight"
                      label="Address" auto-grow rows="3"
                      v-model="newClient.address">
                    </v-textarea>
                  </v-col>
                </v-row>
              </v-form>
              <!-- END FORM -->

              <p v-if="newClientErrors.contact_details" class="red--text">{{newClientErrors.contact_details[0]}}</p>
            </v-container>
          </v-card-text>
          <v-card-actions class="pb-5 pr-8 pt-0">
            <v-spacer></v-spacer>
            <v-btn color="primaryLight" text @click="close()">Cancel</v-btn>
            <v-btn depressed color="primaryLight" class="white--text"
              :loading="createClientLoading" :disabled="createClientLoading || !newClientFormValid" @click="addClient()">
              Add client
              <template v-slot:loader>
                <span>Processing...</span>
              </template>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-toolbar>
    <v-data-table
      :headers="headers"
      :items="clients"
      item-key="id"
      :items-per-page="5"
      :search="search"
      class="mb-5 primaryLight--text elevation-3">

      <!-- Search field -->
      <template v-slot:top>
        <v-text-field
          outlined dense
          v-model="search"
          label="Search by name or contact number"
          append-icon="mdi-magnify"
          class="mx-4 py-3"
          hide-details
        ></v-text-field>
      </template>

      <!-- Column field -->
      <template v-slot:item.name="{ item }">
        <p class="admin__row-field">{{item.name}}</p>
      </template>
      <template v-slot:item.contact_number="{ item }">
        <p class="admin__row-field">{{item.contact_number}}</p>
      </template>
      <template v-slot:item.joined_at="{ item }">
        <p class="admin__row-field">{{item.joined_at}}</p>
      </template>

      <!-- Action field -->
      <template v-slot:item.actions="{ item }">
        <v-icon class="mr-2 green--text" @click="openEditClientDialog(item)">mdi-pencil</v-icon>
        <v-icon class="red--text" @click="openDeleteClientDialog(item)">mdi-delete</v-icon>
      </template>

      <!-- Refetch button -->
      <template v-slot:no-data>
        <v-btn color="primaryLight my-10 white--text" @click="getClients()">Refetch client list</v-btn>
      </template>
    </v-data-table>

    <!-- Detail dialog -->
    <v-dialog v-model="editClientDialog" max-width="500px">
      <v-card>
        <v-card-title class="primaryLight--text">Client basic details</v-card-title>
        <v-card-text class="pb-0">
          <v-container>
            <!-- START FORM -->
            <v-form v-model="editClientFormValid">
              <v-row>

                <!-- START NAME FIELD -->
                <v-col cols="12" class="pt-0">
                  <v-text-field
                    outlined dense color="primaryLight"
                    v-model="editedClient.name" :rules="nameRules"
                    :error-messages="editClientErrors.name[0]"
                    @focus="editClientErrors.name = []"
                    label="Name">
                  </v-text-field>
                </v-col>

                <!-- START NUMBER FIELD -->
                <v-col cols="12" class="pt-0">
                  <v-text-field
                    outlined dense color="primaryLight"
                    v-model="editedClient.contact_number" :rules="numberRules"
                    :error-messages="editClientErrors.contact_number[0]"
                    @focus="editClientErrors.contact_number = []"
                    label="Contact number"
                    prefix="+673">
                  </v-text-field>
                </v-col>

                <!-- START BIRTHDATE FIELD -->
                <v-col cols="12" class="pt-0">
                  <v-text-field
                    outlined dense color="primaryLight"
                    v-model="editedClient.birthdate"
                    hint="Ensure it is in DD/MM/YYYY format"
                    persistent-hint
                    label="Birthdate">
                  </v-text-field>
                </v-col>

                <!-- START ADDRESS FIELD -->
                <v-col cols="12" class="pt-0">
                  <v-textarea
                    outlined dense color="primaryLight"
                    label="Address" auto-grow rows="3"
                    v-model="editedClient.address">
                  </v-textarea>
                </v-col>
              </v-row>
            </v-form>
            <!-- END FORM -->

          </v-container>
        </v-card-text>

        <v-card-actions class="pb-5 pr-8 pt-0">
          <v-spacer></v-spacer>
          <v-btn color="primaryLight" text @click="close()">Cancel</v-btn>
          <v-btn depressed color="primaryLight" class="white--text"
            :loading="editClientLoading" :disabled="editClientLoading || !editClientFormValid" @click="updateClient()">
            Save changes
            <template v-slot:loader>
              <span>Processing...</span>
            </template>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete dialog -->
    <v-dialog v-model="deleteClientDialog" max-width="500px">
      <v-card>
        <v-card-title class="primaryLight--text">Are you sure you want to remove<br> {{deletedClient.name}} from your list?</v-card-title>
        <v-card-actions class="pb-5 pr-8">
          <v-spacer></v-spacer>
          <v-btn color="primaryLight" text @click="close()">Cancel</v-btn>
          <v-btn color="primaryLight white--text" @click="deleteClient()">Yes, remove</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  import moment from 'moment'
  import clientsCollection from '../services/firestore/client'

  export default {
    name: 'ClientsList',
    data() {
      return {
        search: '',
        newClientDialog: false,
        editClientDialog: false,
        deleteClientDialog: false,
        headers: [
          { text: 'Name', value: 'name' },
          { text: 'Contact number', value: 'contact_number'},
          { text: 'First joined', value: 'joined_at'},
          { text: 'Admin actions', value: 'actions', sortable: false }
        ],
        clients: [],
        newClient: {},
        editedClient: {},
        deletedClient: {},

        // Form
        newClientFormValid: false,
        editClientFormValid: false,
        createClientLoading: false,
        editClientLoading: false,
        nameRules: [
          name => !!name || 'What would this client like to be called?'
        ],
        numberRules: [
          number => !!number || 'Please enter a contact number to reach the client.'
        ],
        datepicker: false,
        newClientErrors: {
          name: [],
          contact_number: [],
          contact_details: []
        },
        editClientErrors: {
          name: [],
          contact_number: []
        }
      }
    },
    watch: {
      datepicker(val) {
        val && setTimeout(() => (this.$refs.picker.activePicker = 'YEAR'))
      },
      editClientDialog (val) {
        val || this.close()
      },
      deleteClientDialog (val) {
        val || this.close()
      },
    },
    created() {
      this.getClients()
    },
    methods: {
      setDate(date) {
        this.$refs.datepicker.save(date)
      },
      openNewClientDialog() {
        this.newClient = {}
        this.createClientLoading = false
      },
      getClients() {
        this.clients = clientsCollection.index()
      },
      addClient() {
        this.newClientErrors = {
          name: [],
          contact_number: [],
          contact_details: []
        }
        this.createClientLoading = true
        clientsCollection.create({
          name: this.newClient.name,
          contact_number: this.newClient.contact_number,
          birthdate: new Date(moment(this.newClient.birthdate).format()),
          address: this.newClient.address || 'Undisclosed'
        }).then(() => {
          this.getClients()
          this.createClientLoading = false
          this.close()
        }).catch(err => {
          this.createClientLoading = false
          err = err.response.data
          this.newClientErrors = {
            name: err.name || [], contact_number: err.contact_number || [], contact_details: err.contact_details || []
          }
        })
      },
      openEditClientDialog(client) {
        this.editedClient = client
        this.editClientDialog = true
      },
      updateClient() {
        this.editClientErrors = {
          name: [],
          contact_number: []
        }
        this.editClientLoading = true
        const [day, month, year] = this.editedClient.birthdate.split('/')
        clientsCollection.update({
          id: this.editedClient.id,
          name: this.editedClient.name,
          contact_number: this.editedClient.contact_number,
          birthdate: new Date(moment(`${year}-${month}-${day}`).format()),
          address: this.editedClient.address || 'Undisclosed'
        }).then(() => {
          this.editClientLoading = false
          this.close()
          this.getClients()
        }).catch(err => {
          this.editClientLoading = false
          err = err.response.data
          this.editClientErrors = {
            name: err.name || [], contact_number: err.contact_number || []
          }
        })
      },
      openDeleteClientDialog(client) {
        this.deletedClient = client
        this.deleteClientDialog = true
      },
      deleteClient() {
        clientsCollection.delete(this.deletedClient.id).then(() => {
          this.getClients()
          this.close()
        })
      },
      close() {
        this.newClientDialog = false
        this.editClientDialog = false
        this.deleteClientDialog = false
        this.newClient = {}
        this.editedClient = {}
        this.deletedClient = {}
      }
    }
  }
</script>
